<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import ProviderBadge from './components/ProviderBadge.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MigrateInboxModal from './components/MigrateInboxModal.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { isInboxConnected } from 'dashboard/helper/inbox';
import socialAPI from 'dashboard/api/social';
import marketingAPI from 'dashboard/api/marketing';
import { SOCIAL_PLATFORMS } from 'dashboard/components-next/social/constants';
import { visibleSocialRows } from './helpers/socialRows';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const showMigrateModal = ref(false);

const accountId = computed(() => getters.getCurrentAccountId.value);
// Ensure reactivity by directly depending on accounts state records
const isInboxMigrationEnabled = computed(() => {
  const id = Number(accountId.value);
  const records = store.state.accounts?.records || [];
  const account = records.find(r => r.id === id) || {};
  return Boolean(account.features?.[FEATURE_FLAGS.INBOX_MIGRATION]);
});

const inboxes = useMapGetter('inboxes/getInboxes');

const inboxesList = computed(() => {
  return inboxes.value?.slice().sort((a, b) => a.name.localeCompare(b.name));
});

// Contas de rede conectadas só para publicar também são canal (decisão Ivan
// 08/10: a lista de Canais cuida de toda conexão). As que já alimentam uma
// caixa não repetem: a caixa já está na lista.
const socialAccounts = ref([]);
const socialToRemove = ref(null);
const showSocialDelete = ref(false);

const socialRows = computed(() => visibleSocialRows(socialAccounts.value));

const platformMeta = key => SOCIAL_PLATFORMS.find(pl => pl.key === key) || {};
const platformName = key =>
  t(`SOCIAL_STUDIO.ACCOUNTS.PLATFORMS.${key.toUpperCase()}`);

// Conectar rede não pede módulo (decisão Ivan 08/10): a lista não depende de
// módulo nenhum e a API não responde 402.
const fetchSocialAccounts = async () => {
  try {
    const { data: social } = await socialAPI.accounts();
    socialAccounts.value = social.accounts || [];
  } catch {
    socialAccounts.value = [];
  }
};

// Google Ads também é canal (ocupa 1 vaga do plano). O status vem da tela de
// Conexões do Marketing, que não é cobrada por módulo (sem 402).
const googleAds = ref(null);
const fetchGoogleAds = async () => {
  try {
    const { data } = await marketingAPI.connections();
    googleAds.value = data?.google_ads?.connected ? data.google_ads : null;
  } catch {
    googleAds.value = null;
  }
};

const openSocialDelete = account => {
  socialToRemove.value = account;
  showSocialDelete.value = true;
};
const closeSocialDelete = () => {
  showSocialDelete.value = false;
  socialToRemove.value = null;
};
const confirmSocialDelete = async () => {
  const account = socialToRemove.value;
  closeSocialDelete();
  if (!account) return;
  try {
    await socialAPI.disconnect(account.id);
    useAlert(t('INBOX_MGMT.LIST.SOCIAL_DISCONNECTED'));
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SOCIAL_STUDIO.ACCOUNTS.GENERIC_ERROR')
    );
  }
  fetchSocialAccounts();
};

onMounted(() => {
  if (!isAdmin.value) return;
  fetchSocialAccounts();
  fetchGoogleAds();
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async ({ id }) => {
  try {
    await store.dispatch('inboxes/delete', id);
    useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE')
    );
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="!inboxesList.length && !socialRows.length && !googleAds"
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        feature-name="inboxes"
      >
        <template #actions>
          <router-link
            v-if="isAdmin"
            :to="{ name: 'settings_inbox_new' }"
            data-tour="inboxes-nova"
          >
            <Button
              icon="i-lucide-circle-plus"
              :label="$t('SETTINGS.INBOXES.NEW_INBOX')"
            />
          </router-link>
          <Button
            v-if="isAdmin && isInboxMigrationEnabled"
            icon="i-lucide-shuffle"
            :label="$t('SETTINGS.INBOXES.MIGRATE_INBOX')"
            @click="showMigrateModal = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto">
        <tbody class="divide-y divide-n-weak flex-1 text-n-slate-12">
          <tr v-for="inbox in inboxesList" :key="inbox.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center flex-row gap-4">
                <div
                  v-if="inbox.avatar_url"
                  class="bg-n-alpha-3 rounded-full size-12 p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
                >
                  <Avatar
                    :src="inbox.avatar_url"
                    :name="inbox.name"
                    :size="30"
                    rounded-full
                  />
                </div>
                <div
                  v-else
                  class="size-12 flex justify-center items-center bg-n-alpha-3 rounded-full p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
                >
                  <ChannelIcon class="size-5 text-n-slate-10" :inbox="inbox" />
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <span class="block font-medium capitalize">
                      {{ inbox.name }}
                    </span>
                    <ProviderBadge
                      :channel-type="inbox.channel_type"
                      :provider="inbox.provider"
                      :medium="inbox.medium"
                    />
                  </div>
                  <ChannelName
                    :channel-type="inbox.channel_type"
                    :medium="inbox.medium"
                  />
                  <!-- Status de conexão: reauthorization_required (canais OAuth)
                       + provider_connection.connection (WhatsApp não-oficial) -->
                  <div class="mt-0.5 flex items-center gap-1 text-xs">
                    <span
                      class="size-1.5 rounded-full"
                      :class="
                        isInboxConnected(inbox) ? 'bg-n-teal-9' : 'bg-n-ruby-9'
                      "
                    />
                    <span
                      :class="
                        isInboxConnected(inbox)
                          ? 'text-n-slate-10'
                          : 'text-n-ruby-11 font-medium'
                      "
                    >
                      {{
                        isInboxConnected(inbox)
                          ? $t('INBOX_MGMT.STATUS.CONNECTED')
                          : $t('INBOX_MGMT.STATUS.DISCONNECTED')
                      }}
                    </span>
                  </div>
                  <!-- Pendência de pagamento na Meta (131042): templates recusados -->
                  <div
                    v-if="inbox.meta_payment_issue"
                    v-tooltip.top="
                      $t('INBOX_MGMT.STATUS.META_PAYMENT_ISSUE_TOOLTIP')
                    "
                    class="mt-0.5 flex items-center gap-1 text-xs font-medium text-n-amber-11"
                  >
                    <span class="i-lucide-credit-card size-3.5" />
                    <span>{{
                      $t('INBOX_MGMT.STATUS.META_PAYMENT_ISSUE')
                    }}</span>
                  </div>
                </div>
              </div>
            </td>

            <td class="py-4">
              <div class="flex gap-1 justify-end">
                <router-link
                  :to="{
                    name: 'settings_inbox_show',
                    params: { inboxId: inbox.id },
                  }"
                >
                  <Button
                    v-if="isAdmin"
                    v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                    icon="i-lucide-settings"
                    slate
                    xs
                    faded
                  />
                </router-link>
                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDelete(inbox)"
                />
              </div>
            </td>
          </tr>
          <tr v-for="account in socialRows" :key="`social-${account.id}`">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center flex-row gap-4">
                <div
                  class="size-12 flex justify-center items-center bg-n-alpha-3 rounded-full p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
                >
                  <span
                    class="size-5 text-n-slate-10"
                    :class="platformMeta(account.platform).icon"
                  />
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <span class="block font-medium">
                      {{ account.username || platformName(account.platform) }}
                    </span>
                    <span
                      class="px-2 py-0.5 text-xs font-medium rounded-md uppercase bg-n-alpha-2 text-n-slate-11"
                    >
                      {{ $t('INBOX_MGMT.LIST.SOCIAL_BADGE') }}
                    </span>
                  </div>
                  <span class="text-sm text-n-slate-12">
                    {{ platformName(account.platform) }}
                  </span>
                  <div class="mt-0.5 flex items-center gap-1 text-xs">
                    <span
                      class="size-1.5 rounded-full"
                      :class="
                        account.status === 'connected'
                          ? 'bg-n-teal-9'
                          : 'bg-n-ruby-9'
                      "
                    />
                    <span
                      :class="
                        account.status === 'connected'
                          ? 'text-n-slate-10'
                          : 'text-n-ruby-11 font-medium'
                      "
                    >
                      {{
                        account.status === 'connected'
                          ? $t('INBOX_MGMT.STATUS.CONNECTED')
                          : $t('INBOX_MGMT.STATUS.DISCONNECTED')
                      }}
                    </span>
                  </div>
                </div>
              </div>
            </td>
            <td class="py-4">
              <div class="flex gap-1 justify-end">
                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('INBOX_MGMT.LIST.SOCIAL_DISCONNECT')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openSocialDelete(account)"
                />
              </div>
            </td>
          </tr>
          <tr v-if="googleAds">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center flex-row gap-4">
                <div
                  class="size-12 flex justify-center items-center bg-n-alpha-3 rounded-full p-2 ring ring-n-solid-1 border border-n-strong shadow-sm"
                >
                  <span class="size-5 text-n-slate-10 i-ri-google-fill" />
                </div>
                <div>
                  <div class="flex items-center gap-2">
                    <span class="block font-medium">
                      {{
                        googleAds.username ||
                        $t('INBOX_MGMT.LIST.GOOGLE_ADS_NAME')
                      }}
                    </span>
                    <span
                      class="px-2 py-0.5 text-xs font-medium rounded-md uppercase bg-n-alpha-2 text-n-slate-11"
                    >
                      {{ $t('INBOX_MGMT.LIST.GOOGLE_ADS_BADGE') }}
                    </span>
                  </div>
                  <span class="text-sm text-n-slate-12">
                    {{ $t('INBOX_MGMT.LIST.GOOGLE_ADS_NAME') }}
                  </span>
                  <div class="mt-0.5 flex items-center gap-1 text-xs">
                    <span class="size-1.5 rounded-full bg-n-teal-9" />
                    <span class="text-n-slate-10">
                      {{ $t('INBOX_MGMT.STATUS.CONNECTED') }}
                    </span>
                  </div>
                </div>
              </div>
            </td>
            <td class="py-4">
              <div class="flex gap-1 justify-end">
                <router-link :to="{ name: 'marketing_connections' }">
                  <Button
                    v-tooltip.top="$t('INBOX_MGMT.LIST.GOOGLE_ADS_MANAGE')"
                    icon="i-lucide-settings"
                    slate
                    xs
                    faded
                  />
                </router-link>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
    <woot-delete-modal
      v-model:show="showSocialDelete"
      :on-close="closeSocialDelete"
      :on-confirm="confirmSocialDelete"
      :title="$t('INBOX_MGMT.LIST.SOCIAL_CONFIRM_TITLE')"
      :message="
        $t('INBOX_MGMT.LIST.SOCIAL_CONFIRM_MESSAGE', {
          name: socialToRemove?.username || '',
        })
      "
      :confirm-text="$t('INBOX_MGMT.LIST.SOCIAL_CONFIRM_YES')"
      :reject-text="$t('INBOX_MGMT.LIST.SOCIAL_CONFIRM_NO')"
    />
    <MigrateInboxModal
      v-if="isInboxMigrationEnabled"
      v-model:show="showMigrateModal"
    />
  </SettingsLayout>
</template>
